import isEmpty from 'lodash/isEmpty';
import { parseIntOrZero } from '~/core/util/string';
import { isNonBookable } from './getSelfPayRowData';
import { isV1Service } from './matchServices';
import { Service, ServicePrices } from './Service';

// TODO: This should just be `ServicesObj`. But, that type is wrong
// as it's a string -> Service instead of string -> Service[]
export const getServicePrices = (servicesObj: Record<string, Service[]> = {}) => {
  return Object.values(servicesObj).reduce<ServicePrices>(
    (servicePrices, value) =>
      value.reduce((servicePrices, service) => {
        if (
          service?.details?.accept_cash &&
          (service.price || (service.min_price && service.max_price))
        ) {
          return {
            ...servicePrices,
            [service.name]: {
              name: service.display_name ?? '',
              price: service.price || '',
              maxPrice: service.max_price || '',
              minPrice: service.min_price || '',
              hasPriceRange: !!service?.details?.has_price_range,
              requiresBaseOfficeVisitFee: !!service?.details?.requires_base_office_visit_fee,
              isNonBookable: isNonBookable(service),
              acceptCash: !!service?.details?.accept_cash,
              acceptInsurance: !!service?.details?.accept_insurance,
              walkinsAccepted: !!service?.details?.walkins_accepted,
              notes: service.notes || '',
            },
          };
        }
        return servicePrices;
      }, servicePrices),
    {}
  );
};

export const getAllV1ServicePrices = (servicesObj: Record<string, Service[]> = {}) => {
  let hasKnownPrices = false;

  const servicePriceData = Object.values(servicesObj).reduce<ServicePrices>(
    (servicePrices, value) =>
      value.reduce((servicePrices, service) => {
        if (service.price || service.min_price || service.max_price) {
          hasKnownPrices = true;
        }
        if (isV1Service(service.name)) {
          return {
            ...servicePrices,
            [service.name]: {
              name: service.display_name ?? '',
              price: service.price || '',
              maxPrice: service.max_price || '',
              minPrice: service.min_price || '',
              hasPriceRange: !!service?.details?.has_price_range,
              requiresBaseOfficeVisitFee: !!service?.details?.requires_base_office_visit_fee,
              isNonBookable: isNonBookable(service),
              acceptCash: !!service?.details?.accept_cash,
              acceptInsurance: !!service?.details?.accept_insurance,
              walkinsAccepted: !!service?.details?.walkins_accepted,
              notes: service.notes || '',
            },
          };
        }

        return servicePrices;
      }, servicePrices),
    {}
  );

  servicePriceData.hasKnownPrices = hasKnownPrices;
  return servicePriceData;
};

/**
 * Utility function for getting total price range for a service depending on whether it needs office fee
 *
 * @param servicesObj servicesObj from location object
 * @param serviceName the exact service name (e.g laceration_repair)
 * @returns a string with the total price range (e.g "$12-15" or "Price unknown")
 */
export const getServicePriceRange = (
  servicesObj: Record<string, Service[]> = {},
  serviceName: string
) => {
  const pricesObj = getServicePrices(servicesObj);
  const priceObj = pricesObj[serviceName];
  const officePriceObj = pricesObj.base_office_visit;

  if (isEmpty(priceObj)) return 'Price unknown';

  const displayPrice = (price: number, minPrice: number, maxPrice: number, hasRange: boolean) => {
    if (hasRange && minPrice && maxPrice) return `$${minPrice}-${maxPrice}`;
    if (!price && price !== 0) return 'Price unknown';
    return `$${price}`;
  };

  const servicePrice = parseIntOrZero(priceObj.price);
  const serviceMinPrice = parseIntOrZero(priceObj.minPrice);
  const serviceMaxPrice = parseIntOrZero(priceObj.maxPrice);

  if (priceObj.requiresBaseOfficeVisitFee) {
    if (isEmpty(officePriceObj))
      return `${displayPrice(
        servicePrice,
        serviceMinPrice,
        serviceMaxPrice,
        priceObj.hasPriceRange
      )} (+ office visit fee)`;
    return displayPrice(
      parseIntOrZero(officePriceObj.price) + servicePrice,
      parseIntOrZero(officePriceObj.minPrice) + serviceMinPrice,
      parseIntOrZero(officePriceObj.maxPrice) + serviceMaxPrice,
      officePriceObj.hasPriceRange || priceObj.hasPriceRange
    );
  }

  return displayPrice(servicePrice, serviceMinPrice, serviceMaxPrice, priceObj.hasPriceRange);
};
